/**
 * Student Loan Review Summary — Phase 1 on-screen client-facing preview.
 *
 * READ-ONLY DISPLAY ONLY. No PDF, no save, no print, no send.
 * Uses calculator data already on screen; never re-runs math.
 *
 * Compliance rules:
 *  • A+ CaseFlow ID only — never Skywave Case ID
 *  • Household size and filing status are NEVER shown
 *  • "Estimated" language only — no "approved/guaranteed/will be"
 *  • Graceful "Review needed" / "Not provided" for missing fields
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LOGO_URL } from "@/components/Logo";
import { CheckCircle2 } from "lucide-react";

const fmt$ = (n) =>
    n == null || n === "" || Number.isNaN(Number(n))
        ? null
        : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }); }
    catch { return d; }
};

const today = () => new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

// Plans we show in the comparison rail, in canonical display order
const COMPARISON_PLANS = [
    { key: "standard", label: "Standard",  match: ["Standard Repayment"], term: "10 years" },
    { key: "extended", label: "Extended",  match: ["Extended Standard Repayment", "Extended Repayment"], term: "25 years" },
    { key: "ibr",      label: "IBR",       match: ["Income-Based Repayment", "IBR"], term: "Review" },
    { key: "paye",     label: "PAYE",      match: ["Pay As You Earn", "PAYE"], term: "Review" },
    { key: "icr",      label: "ICR",       match: ["Income-Contingent Repayment", "ICR"], term: "Review" },
];

const REVIEWED = [
    "Federal loan information",
    "Income information used for estimate",
    "Available repayment options",
    "Estimated monthly payment range",
    "Required documentation",
    "Potential next steps",
];

const NEXT_STEPS = [
    "Confirm current loan/account details",
    "Prepare required federal forms",
    "Submit or update repayment request",
    "Monitor servicer processing",
    "Assist with future annual updates",
];

const DISCLAIMER =
    "Payment estimates are based on the information available at the time of review. " +
    "Final eligibility, payment amount, and program approval are determined by the federal " +
    "loan servicer and/or the Department of Education. Changes in income, loan status, or " +
    "program rules may affect the final result.";

export default function ClientSummaryPreview({ open, onOpenChange, client, profile, loansData, results, selected }) {
    // Always recompute on open — no stale state across client switches
    if (!open) return null;

    const v = (x) => (x == null || x === "" ? null : x);
    const NA = "Not provided";
    const REVIEW = "Review needed";

    // Hero recommendation: prefer the selected program; fall back to the first
    // eligible calculated result that beats Standard; else "Review Needed".
    const recommended = pickRecommended(selected, results);

    // Standard payment is read from selected.standard_payment when available,
    // otherwise the calculated Standard row in the results table.
    const standardPay =
        v(selected?.standard_payment) ??
        v(results?.find(r => r.plan_name === "Standard Repayment")?.monthly_payment);

    const monthlyPay = recommended?.monthly_payment ?? null;
    const savings =
        monthlyPay != null && standardPay != null && Number(standardPay) > Number(monthlyPay)
            ? Number(standardPay) - Number(monthlyPay)
            : null;

    const totalBalance = loansData?.totals?.included?.total_balance ?? loansData?.totals?.all?.total_balance ?? null;
    const loanCount = loansData?.totals?.included?.count ?? loansData?.totals?.all?.count ?? (loansData?.loans?.length || 0);

    const reviewDate = today();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto p-0 gap-0 bg-slate-50" data-testid="client-summary-preview">
                <DialogHeader className="sr-only">
                    <DialogTitle>Student Loan Review Summary</DialogTitle>
                </DialogHeader>

                {/* ─── A. Header Banner ───────────────────────────────────── */}
                <div className="bg-gradient-to-br from-[#0c2a4d] to-[#1e4d8c] text-white px-7 py-6 rounded-t-md">
                    <div className="flex items-center gap-3 mb-4">
                        <img src={LOGO_URL} alt="A+ Student Loans" className="h-10 w-10 rounded-md bg-white/10 p-1 object-contain" />
                        <div className="font-display font-semibold tracking-tight text-[15px]">A+ Student Loans</div>
                    </div>
                    <div className="text-2xl font-semibold tracking-tight">Student Loan Review Summary</div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-y-2 gap-x-6 text-[13px]">
                        <HeaderRow label="Prepared for" value={v(client?.client_name) || NA} testId="hdr-name" />
                        <HeaderRow label="A+ CaseFlow ID" value={v(client?.acos_id) || REVIEW} mono testId="hdr-acos-id" />
                        <HeaderRow label="Review Date" value={reviewDate} testId="hdr-date" />
                        <HeaderRow label="Assigned Specialist" value={v(client?.processor_name) || v(client?.assigned_specialist) || v(client?.assigned_rep) || REVIEW} testId="hdr-specialist" />
                    </div>
                </div>

                {/* ─── B. Hero Recommendation Card ────────────────────────── */}
                <div className="px-7 -mt-5">
                    <div className="rounded-xl bg-white shadow-md border border-slate-200 px-6 py-5" data-testid="hero-card">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Recommended Program</div>
                        <div className="text-xl font-semibold text-[#0c2a4d] mt-0.5" data-testid="hero-program">
                            {recommended?.plan_name || "Review Needed"}
                        </div>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <HeroStat
                                label="Estimated Monthly Payment"
                                value={monthlyPay != null ? `${fmt$(monthlyPay)} / month` : REVIEW}
                                accent="navy"
                                testId="hero-monthly"
                            />
                            <HeroStat
                                label="Estimated Monthly Savings"
                                value={savings != null ? `${fmt$(savings)} / month` : REVIEW}
                                accent="green"
                                sub={standardPay != null ? `vs estimated Standard of ${fmt$(standardPay)}` : null}
                                testId="hero-savings"
                            />
                            <HeroStat
                                label="Estimated Standard Payment"
                                value={standardPay != null ? `${fmt$(standardPay)} / month` : REVIEW}
                                accent="muted"
                                testId="hero-standard"
                            />
                        </div>
                    </div>
                </div>

                <div className="px-7 py-6 space-y-5">
                    {/* ─── C. Client Snapshot ─────────────────────────────── */}
                    <SectionCard title="Client Snapshot" testId="client-snapshot">
                        <Grid>
                            <KV label="Client Name"          value={v(client?.client_name) || NA} testId="cs-name" />
                            <KV label="A+ CaseFlow ID"       value={v(client?.acos_id) || REVIEW} mono testId="cs-acos" />
                            <KV label="Phone"                value={v(client?.phone_e164) || v(client?.phone_raw) || NA} testId="cs-phone" />
                            <KV label="Email"                value={v(client?.email) || NA} testId="cs-email" />
                            <KV label="State"                value={v(profile?.state) || v(client?.state) || NA} testId="cs-state" />
                            <KV label="Assigned Specialist"  value={v(client?.processor_name) || v(client?.assigned_specialist) || v(client?.assigned_rep) || REVIEW} testId="cs-specialist" />
                            <KV label="Review Date"          value={reviewDate} testId="cs-date" />
                        </Grid>
                    </SectionCard>

                    {/* ─── D. Loan Snapshot ───────────────────────────────── */}
                    <SectionCard title="Loan Snapshot" testId="loan-snapshot">
                        <Grid>
                            <KV label="Total Loan Balance"   value={totalBalance != null ? fmt$(totalBalance) : REVIEW} testId="ls-balance" />
                            <KV label="Number of Loans"      value={loanCount > 0 ? String(loanCount) : REVIEW} testId="ls-count" />
                            <KV label="Current Program"      value={v(client?.program_type) || REVIEW} testId="ls-program" />
                            <KV label="Current Loan Status"  value={v(client?.client_status)?.replace(/_/g, " ") || REVIEW} testId="ls-status" />
                            <KV label="Estimated Standard Payment" value={standardPay != null ? `${fmt$(standardPay)} / month` : REVIEW} testId="ls-standard" />
                        </Grid>
                    </SectionCard>

                    {/* ─── E. Repayment Option Comparison ─────────────────── */}
                    <SectionCard title="Repayment Option Comparison" testId="repayment-comparison">
                        <div className="overflow-hidden rounded-lg border border-slate-200">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr className="text-left text-[11px] uppercase tracking-wider">
                                        <th className="py-2.5 px-3 font-semibold">Plan</th>
                                        <th className="py-2.5 px-3 text-right font-semibold">Estimated Monthly</th>
                                        <th className="py-2.5 px-3 font-semibold">Term / Review</th>
                                        <th className="py-2.5 px-3 font-semibold">Fit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {COMPARISON_PLANS.map((p) => {
                                        const row = pickPlan(results, p.match);
                                        const isRecommended = recommended && p.match.includes(recommended.plan_name);
                                        const badge = computeFitBadge(p.key, row, isRecommended);
                                        return (
                                            <tr key={p.key} className="border-t border-slate-100" data-testid={`row-${p.key}`}>
                                                <td className="py-2.5 px-3 font-medium text-slate-900">{p.label}</td>
                                                <td className="py-2.5 px-3 text-right text-slate-900 font-medium">
                                                    {row?.monthly_payment != null ? `${fmt$(row.monthly_payment)} / mo` : REVIEW}
                                                </td>
                                                <td className="py-2.5 px-3 text-slate-600 text-[13px]">
                                                    {row?.term_months ? `${Math.round(row.term_months / 12)} years` : p.term}
                                                </td>
                                                <td className="py-2.5 px-3"><FitBadge kind={badge} /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </SectionCard>

                    {/* ─── F. What A+ Reviewed ────────────────────────────── */}
                    <SectionCard title="What A+ Reviewed" testId="what-reviewed">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
                            {REVIEWED.map((item) => (
                                <li key={item} className="flex items-start gap-2.5 text-[13px] text-slate-800">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </SectionCard>

                    {/* ─── G. Next Steps Timeline ─────────────────────────── */}
                    <SectionCard title="Your Next Steps" testId="next-steps">
                        <ol className="space-y-2.5">
                            {NEXT_STEPS.map((step, i) => (
                                <li key={step} className="flex items-start gap-3">
                                    <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#0c2a4d] text-white text-xs font-semibold shrink-0">
                                        {i + 1}
                                    </span>
                                    <span className="text-[13px] text-slate-800 pt-1">{step}</span>
                                </li>
                            ))}
                        </ol>
                    </SectionCard>

                    {/* ─── H. Disclaimer ──────────────────────────────────── */}
                    <div className="text-[11px] text-slate-500 leading-relaxed pt-2" data-testid="summary-disclaimer">
                        {DISCLAIMER}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ── helpers ──────────────────────────────────────────────────────────── */

function pickRecommended(selected, results) {
    // 1) Selected program (most authoritative)
    if (selected?.selected_plan_name && selected?.monthly_payment != null) {
        return { plan_name: selected.selected_plan_name, monthly_payment: selected.monthly_payment };
    }
    // 2) Eligible calculated result, lowest monthly that beats Standard
    const std = results?.find(r => r.plan_name === "Standard Repayment");
    const candidates = (results || []).filter(r =>
        r.plan_name !== "Standard Repayment" &&
        r.monthly_payment != null &&
        (r.eligible_status === "eligible" || r.eligible_status === "manual")
    );
    if (!candidates.length) return null;
    candidates.sort((a, b) => Number(a.monthly_payment) - Number(b.monthly_payment));
    const best = candidates[0];
    if (std?.monthly_payment != null && Number(best.monthly_payment) >= Number(std.monthly_payment)) return null;
    return best;
}

function pickPlan(results, names) {
    if (!Array.isArray(results)) return null;
    return results.find(r => names.includes(r.plan_name)) || null;
}

function computeFitBadge(planKey, row, isRecommended) {
    if (planKey === "standard") return "baseline";
    if (isRecommended) return "recommended";
    if (!row) return "review";
    if (row.eligible_status === "not_eligible") return "not_eligible";
    if (row.eligible_status === "needs_review") return "review";
    if (row.monthly_payment == null) return "review";
    return "available";
}

function FitBadge({ kind }) {
    const styles = {
        recommended:  "bg-emerald-100 text-emerald-800 border-emerald-300",
        available:    "bg-sky-50 text-sky-700 border-sky-200",
        baseline:     "bg-slate-100 text-slate-700 border-slate-200",
        review:       "bg-amber-50 text-amber-800 border-amber-200",
        not_best_fit: "bg-slate-100 text-slate-600 border-slate-200",
        not_eligible: "bg-rose-50 text-rose-700 border-rose-200",
    };
    const labels = {
        recommended:  "Recommended",
        available:    "Available",
        baseline:     "Baseline",
        review:       "Review Needed",
        not_best_fit: "Not Best Fit",
        not_eligible: "Not Eligible",
    };
    return (
        <span className={`inline-flex items-center text-[10.5px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${styles[kind]}`}>
            {labels[kind]}
        </span>
    );
}

function SectionCard({ title, children, testId }) {
    return (
        <div className="rounded-xl bg-white shadow-sm border border-slate-200 px-5 py-4" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-3">{title}</div>
            {children}
        </div>
    );
}

function Grid({ children }) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">{children}</div>;
}

function KV({ label, value, mono = false, testId }) {
    return (
        <div className="flex flex-col" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`text-[13px] text-slate-900 ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}

function HeaderRow({ label, value, mono = false, testId }) {
    return (
        <div className="flex flex-col" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-white/70">{label}</div>
            <div className={`text-[13.5px] text-white ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}

function HeroStat({ label, value, sub, accent, testId }) {
    const tone =
        accent === "green" ? "text-emerald-700"
      : accent === "navy"  ? "text-[#0c2a4d]"
      :                      "text-slate-700";
    return (
        <div className="space-y-0.5" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
            <div className={`text-2xl font-semibold tracking-tight ${tone}`}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
        </div>
    );
}
